/**
 * Pure math for the right-hand look stick: converts a normalized stick
 * deflection (x right-positive, y up-positive, magnitude <= 1) plus a frame
 * delta into camera yaw/pitch deltas, rate-based like a gamepad right stick.
 * Kept free of DOM/Babylon imports so it stays unit-testable.
 */

/** Radial deflection below which the stick is treated as centered. */
export const LOOK_DEADZONE: number = 0.15;
/** Yaw rate at full deflection, radians per second. */
export const MAX_YAW_SPEED: number = 2.5;
/** Pitch rate at full deflection, radians per second. */
export const MAX_PITCH_SPEED: number = 1.8;

export interface LookDelta {
  dyaw: number;
  dpitch: number;
}

/**
 * Stick right (+x) yields positive dyaw (turn right). Stick up (+y) yields
 * negative dpitch (look up; positive camera rotation.x pitches down). Speed
 * ramps from zero at the deadzone edge with a squared curve so small
 * deflections give fine aim while full tilt turns fast.
 */
export function lookStickDelta(nx: number, ny: number, dtSeconds: number): LookDelta {
  const mag: number = Math.hypot(nx, ny);
  if (mag <= LOOK_DEADZONE) {
    return { dyaw: 0, dpitch: 0 };
  }
  const clamped: number = Math.min(mag, 1);
  const ramp: number = (clamped - LOOK_DEADZONE) / (1 - LOOK_DEADZONE);
  const curve: number = ramp * ramp;
  const ux: number = nx / mag;
  const uy: number = ny / mag;
  return {
    dyaw: ux * curve * MAX_YAW_SPEED * dtSeconds,
    dpitch: -uy * curve * MAX_PITCH_SPEED * dtSeconds,
  };
}
